import { uid } from "quasar";
import Vue from "vue";
import { $http } from "@/assets/js/appFunc";
import i18n from "@/i18n";
import router from "@/pages/main/router";

export * from "./permissions/actions";

/**
 * Searches for current app in sessionStorage if not present in store
 * If it finds something in sessionStorage it also sets it in apps/currentApp and main/currentApplicationId
 * @param context
 */
export const findCurrentApp = context => {
    if (!context.state.currentApp.id) {
        let sessionSavedApp = sessionStorage.getItem("currentApp");

        if (sessionSavedApp) {
            sessionSavedApp = JSON.parse(sessionSavedApp);
            context.commit("setCurrentApp", sessionSavedApp);
            // eslint-disable-next-line no-param-reassign
            context.rootState.main.currentApplicationId = sessionSavedApp.id;
        }
    }
};

/**
 * Searches for current process in sessionStorage if not present in store
 * If it finds something in sessionStorage it also sets it in apps/currentProcess and main/currentProcessId
 * @param context
 */
export const findCurrentProcess = context => {
    if (context.state.currentProcess && !context.state.currentProcess.id) {
        let sessionSavedProcess = sessionStorage.getItem("currentProcess");

        if (sessionSavedProcess && sessionSavedProcess !== "undefined") {
            sessionSavedProcess = JSON.parse(sessionSavedProcess);
            context.commit("setCurrentProcess", sessionSavedProcess);
            context.rootState.main.currentProcessId = sessionSavedProcess.id;
        }
    }
};

export const archiveApplication = (context, payload) => {
    context.commit("setArchivingCurrentAppStatus", { data: { id: payload.id, publishStatus: "ARCHIVE_IN_PROGRESS" } });
    const params = {
        type: "GET",
        url: `/engine/api/application/archive/${payload.id}`
    };
    return $http(params, true).then(
        () => {},
        () => {
            context.commit("setArchivingCurrentAppStatus", { data: { id: payload.id, publishStatus: payload.status } });
        }
        // () => { setTimeout(() => { context.commit('setArchivingCurrentAppStatus', { data: { id: payload.id, publishStatus: payload.status } }) }, 1000) }
    );
};

export const getAppOwnerInfo = (context, payload) => {
    const params = {
        type: "GET",
        url: `/identity/tenants/users/${payload}`
    };
    return $http(params, true);
};

export const getAllApps = context => {
    const params = {
        type: "GET",
        url: "/engine/dashboard/my/apps",
        data: ""
    };
    return $http(params, true).then(
        data => context.commit("setAllApps", data),
        error => console.log(error)
    );
};

export const getAppsWithPagination = (store, payload) => {
    const addSearchValue = payload.searchValue ? `&searchValue=${payload.searchValue}` : "";
    const addPageNumber = `?pageNumber=${payload.pageNumber}&pageSize=${payload.pageSize}`;
    let addQueryParam = addPageNumber + addSearchValue;
    if (addQueryParam) {
        addQueryParam = `?${addQueryParam.substr(1)}`;
    }
    const params = {
        url: `/engine/dashboard/apps${addQueryParam}`,
        type: "GET"
    };
    return new Promise((resolve, reject) => {
        $http(params, true).then(
            data => {
                const result = data.data || data;
                resolve(result);
            },
            error => {
                reject(error);
                console.log(error);
            }
        );
    });
};

export const getAppsByIds = (store, payload) => {
    const params = {
        url: "/engine/api/applications",
        type: "POST",
        data: payload
    };
    return new Promise((resolve, reject) => {
        $http(params, true).then(
            data => {
                const result = data.data || data;
                resolve(result);
            },
            error => {
                reject(error);
                console.log(error);
            }
        );
    });
};

export const getPagedApps = (context, payload) => {
    const { resetAppList } = payload || {};
    if (resetAppList) {
        delete payload.resetAppList;
    }
    const params = {
        type: "POST",
        url: "/engine/dashboard/my/apps/paged",
        data: payload
    };
    return new Promise((resolve, reject) => {
        $http(params, true).then(
            data => {
                if (!payload.noStorage) {
                    context.commit("setPagedApps", { ...data, ...(resetAppList ? { resetAppList } : {}) });
                    context.commit("setHasMore", data.data.apps.length === 18);
                }
                resolve(data);
            },
            error => { reject(error); }
        );
    });
};

export const getAppsNumber = context => {
    const params = {
        type: "GET",
        url: "/engine/dashboard/my/apps/count",
        data: ""
    };
    return $http(params, true).then(
        data => context.commit("setAllAppsNumber", data.data),
        error => console.log(error)
    );
};

export const getApplicationByProcessId = (context, id) => {
    const params = {
        type: "GET",
        url: `/engine/api/application/processDefinitionId/${id}`,
        noNotify: true
    };
    return $http(params, true);
};

export const getApplicationByPerspectiveId = (context, id) => {
    const params = {
        type: "GET",
        url: `/engine/api/application/perspectives/${id}`,
        noNotify: true
    };
    return $http(params, true);
};

export const exportApplication = (context, item) => {
    let filename;
    const applicationId = item.id ? item.id : context.state.currentApp.id;
    const params = {
        type: "GET",
        url: `/engine/api/exportApplication/application/${applicationId}`,
        data: item,
        responseType: "arraybuffer",
        noNotify: true,
        headers: {
            "Content-type": "application/octet-stream"
        }
    };

    return new Promise((resolve, reject) => {
        $http(params, true)
            .then(data => {
                const url = window.URL.createObjectURL(new Blob([data.data], { type: "application/octet-stream" }));
                const link = document.createElement("a");

                // eslint-disable-next-line prefer-destructuring
                filename = data.headers["content-disposition"].split("=")[1];

                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                resolve();
            })
            .catch(error => {
                reject(error);
            });
    });
};

export const importApplication = (context, item) => {
    const params = {
        type: "POST",
        url: "/engine/api/importApplication",
        data: item
    };
    return $http(params, true);
};

export const checkDuplicatedApplication = (context, item) => {
    const params = {
        type: "POST",
        url: "/engine/api/importApplication/canBeUpdated",
        data: item
    };
    return $http(params, true);
};

export const updateApplication = (context, payload) => {
    const { id } = payload;
    const params = {
        type: "POST",
        url: `/engine/api/updateApplication/application/${id}`,
        data: payload.file,
        noNotify: true
    };
    return $http(params, true);
};

export const getAppInstancesInformation = (context, id) => {
    const params = {
        type: "GET",
        url: `/engine/api/application/${id}/instance-info`
    };
    return $http(params, true);
};

export const getProcessInstancesInformation = (context, processId) => {
    const params = {
        type: "GET",
        url: `/engine/api/processDefinition/${processId}/instance-info`
    };
    return $http(params, true);
};

export const addEditApp = (context, payload) => {
    const formData = new FormData();
    if (payload.image) formData.append("image", payload.image);
    const data = {
        appCreatorName: payload.appCreatorName || null,
        id: payload.id || null,
        versionId: payload.appVersion,
        type: payload.type,
        active: payload.active || true,
        name: payload.name,
        blockchainEnabled: payload.id ? payload.blockchainEnabled : payload.blockchainType !== "STANDARD",
        code: payload.code || uid(),
        description: payload.description,
        iteration: payload.iteration || 0,
        canManage: payload.id ? payload.canManage : null,
        canView: payload.id ? payload.canView : null,
        canStart: payload.id ? payload.canStart : null,
        imageId: payload.id ? payload.imageId : null,
        imageUrl: payload.id ? payload.imageUrl : null,
        processInformation: payload.processInformation || null,
        publishStatus: payload.publishStatus || null
    };

    if (payload.id) {
        data.blockchainType = payload.blockchainType;
    } else if (data.blockchainEnabled && payload.blockchainType === "BLOCKCHAIN") {
        data.blockchainType = payload.blockchainType;
    } else {
        data.blockchainType = "STANDARD";
    }

    formData.append("application", JSON.stringify(data));
    const addUrl = "/engine/api/application/insert";
    return $http(
        {
            type: "POST",
            url: addUrl,
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data"
            }
        },
        true
    ).then(
        serverData => {
            if (!context.state.appsNumber) {
                context.commit("setAllAppsNumber", 1);
            }
            let newData = {};
            if (payload.image) {
                newData = { id: serverData.data.id, imageId: serverData.data.imageId, imageUrl: serverData.data.imageUrl };
            }
            const objToSend = { ...data, ...newData };
            delete objToSend.image;
            if (payload.id) {
                context.commit("updatedApp", { ...objToSend });
            } else {
                context.dispatch("actGetCurrentAppUserPermissions", serverData.data.id);
            }
            if (serverData.data.id !== null) {
                objToSend.id = serverData.data.id;
            }

            Vue.prototype.$toast.showNotify({
                type: "success",
                message: i18n.t("generic.saved")
            });
            objToSend.permission = serverData.data.permission;
            return objToSend;
        },
        error => {
            console.log(error);
        }
    );
};
export const saveImageApp = (context, payload) => {
    const formData = new FormData();
    const applicationId = payload.id || context.state.currentApp.id;
    if (payload.image) formData.append("image", payload.image);
    return $http(
        {
            type: "POST",
            url: `/engine/api/application/${applicationId}/update/avatar`,
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data"
            }
        },
        true
    ).then(
        response => response,
        error => {
            console.log("error", error);
        }
    );
};

// eslint-disable-next-line consistent-return
export const saveApp = (context, payload) => {
    const formData = new FormData();
    const applicationId = payload.id || context.state.currentApp.id;
    if (payload.settings.image) formData.append("image", payload.settings.image);
    const data = {
        name: payload.settings.name,
        description: payload.settings.description,
        retention: payload.settings.retention
    };
    formData.append("settings", JSON.stringify(data));
    const addUrl = `/engine/api/application/${applicationId}/settings?updateCover=${payload.settings.imageToRemove || !!payload.settings.image}`;
    return $http(
        {
            type: "POST",
            url: addUrl,
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data"
            }
        },
        true
    ).then(
        response => response.data,
        error => {
            console.log(error);
        }
    );
};

export const setDefaultValuesForNewApp = context => {
    context.commit("setCurrentApp", {
        blockchainType: "STANDARD",
        id: null,
        versionId: null,
        ownerId: null,
        imageId: null,
        imageUrl: null,
        type: null,
        code: null,
        name: null,
        description: null,
        iteration: null,
        canStart: null,
        canManage: null,
        canView: null,
        modifiedOn: null,
        active: null,
        blockchainEnabled: null,
        interfaces: null,
        publishStatus: null
    });
};

export const startApp = (store, appProps) => {
    let idx; let
        signUpEmail = "";
    if (appProps) {
        idx = appProps.appId;
        signUpEmail = appProps.emailSignUp || "";
    }

    const registerAppId = localStorage.getItem("registerAppId");
    $http({
        url: registerAppId ? `/engine/public/api/processDefinition/${idx}/email/${signUpEmail}/start` : `/engine/api/processDefinition/${idx}/start`,
        type: "GET",
        noNotify: !registerAppId
    }, true).then(({ data }) => {
        if (data.guiId && data.taskId && !registerAppId) {
            store.dispatch("components/claimTask", data.taskId, { root: true }).then(response => {
                const req = {
                    type: "GET",
                    url: `/engine/task/get/by/${data.taskId}`
                };

                $http(req, true).then(
                    rsp => {
                        const taskDetails = {
                            canUnclaim: response.data.canUnclaim,
                            __dashboard: true,
                            ...data
                        };
                        taskDetails.activityName = rsp.data.subject;

                        if (router) {
                            router.push({ path: `/tasks/${taskDetails.taskId}` });
                        }
                    },
                    error => {
                        console.log(error);
                    }
                );
            });
        } else if (!registerAppId) {
            Vue.prototype.$toast.showNotify({
                type: "success",
                message: i18n.t("apps.noPermisionsToStartApp")
            });
        }
    })
        .catch(error => {
            if (error && error.response && error.response.status === 401) {
                Vue.prototype.$toast.showNotify({
                    type: "error",
                    message: i18n.t("apps.noPermissionsToStartProcess")
                });
            }
        });
};

export const saveProcess = (context, payload) => {
    const req = {
        type: "POST",
        url: "/design/process/persist",
        data: payload
    };
    return $http(req, true);
};

export const saveProcessSettings = (context, payload) => {
    const req = {
        type: "POST",
        url: "/design/process/updateSettings",
        data: payload
    };
    return $http(req, true);
};

export const updateProcessInstance = (context, payload) => {
    const req = {
        type: "POST",
        url: "/engine/api/processDefinition/audit/user-behaviour",
        data: payload
    };
    return $http(req, true);
};

export const getCodeByAppName = (context, payload) => {
    const params = {
        type: "POST",
        url: "/engine/api/application/code",
        data: payload
    };
    return $http(params, true);
};

export const isDuplicatedCode = (context, payload) => {
    const params = {
        type: "GET",
        url: `/engine/api/application/code/check/${payload}`
    };
    return $http(params, true);
};

export const getAppSettingsInfo = (context, payload) => {
    const applicationId = payload.id || context.state.currentApp.id;
    return $http(
        {
            type: "GET",
            url: `/engine/api/application/${applicationId}`
        },
        true
    ).then(
        response => response,
        error => error
    );
};

export const getAppProcesses = (context, payload) => {
    const params = {
        type: "POST",
        url: "/design/processes",
        data: payload
    };
    return $http(params, true);
};

export const actDiagramExternalPlugins = (context) => {
    const params = {
        type: "GET",
        url: "/engine/api/plugins"
    };
    return new Promise((resolve, reject) => {
        $http(params, true).then(
            data => {
                const setData = {
                    activities: [],
                    events: [],
                    gateways: []
                };
                (data.data || data || []).forEach(elem => {
                    const iconObj = JSON.parse(elem.metadata);
                    const iconDecoded = window.atob(iconObj.icon);
                    const blob = new Blob([iconDecoded], { type: "image/svg+xml" });
                    const url = URL.createObjectURL(blob);

                    const newPlugin = {
                        id: elem.type,
                        output: elem.outputConnectors || 1,
                        input: elem.inputConnectors || 0,
                        shape: {
                            type: "Image",
                            source: url,
                            scale: "Meet"
                        },
                        text: elem.name
                    };
                    setData.events.push(newPlugin);
                });
                context.commit("setDiagramExternalPlugins", setData);
                resolve();
            },
            error => {
                reject(error);
            }
        );
    });
};

export const getProcessesForReports = (context, payload) => {
    const params = {
        type: "GET",
        url: `/engine/api/processes/by/application/${payload}`
    };
    return $http(params, true);
};

export const getAppProcessesPI = (context, payload) => {
    const params = {
        type: "GET",
        url: `/design/processes/inspector?applicationId=${payload.appId}&archived=${payload.archived}`
    };
    return $http(params, true);
};

export const getProcessesDiagramContext = (context, payload) => {
    const processId = payload.fromUiBuilder ? "" : context.state.currentProcess.id;
    const params = {
        type: "GET",
        url: `/design/processes/for-mapping/applicationId/${payload.id}?processDefinitionId=${processId}`
    };
    return $http(params, true);
};
export const duplicateProcesses = (context, payload) => {
    const params = {
        type: "GET",
        url: `/design/process/${payload}/duplicate`
    };
    return $http(params, true);
};
export const renameProcess = (context, payload) => {
    const params = {
        url: "/design/processes/rename",
        type: "POST",
        data: payload
    };
    return $http(params, true);
};
export const getProcessesStartContext = (context, payload) => {
    const params = {
        type: "POST",
        url: "/engine/api/startable/processes/by/application",
        data: payload
    };
    return $http(params, true);
};
export const getProcessesEnrollmentContext = (context, payload) => {
    const params = {
        type: "GET",
        url: `/engine/api/self-enrollment/processes/${payload}`
    };
    return $http(params, true);
};
export const getUpdatedProcessName = (context, payload) => {
    const params = {
        type: "GET",
        url: `/design/processes/${payload}/name`
    };
    return $http(params, true);
};

export const actMarkForArchiving = (context, processId) => {
    const params = {
        type: "GET",
        url: `/design/process/${processId}/mark-for-archive`
    };
    return $http(params, true);
};

export const actCancelArchiving = (context, processId) => {
    const params = {
        type: "GET",
        url: `/design/process/${processId}/unmark-for-archive`
    };
    return $http(params, true);
};

export const actDeleteProcess = (context, processId) => {
    const params = {
        type: "DELETE",
        url: `/design/process/${processId}`
    };
    return $http(params, true);
};

export const getReportsProcesses = (context, payload) => {
    const params = {
        type: "GET",
        url: `/design/processes/reports-perspective?appId=${payload.applicationId}`
    };
    return $http(params, true);
};

export const recoverProcess = (store, payload) => {
    const params = {
        url: "/design/processes/recover",
        type: "POST",
        data: payload
    };
    return $http(params, true);
};
